import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { personService } from '@shared/services/personService';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';

export const useEmailForm = (person) => {
    const { t } = useTranslation();
    const [emailData, setEmailData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editingEmail, setEditingEmail] = useState(null);
    const [validationMessage, setValidationMessage] = useState('');

    const emailValidationSchema = yup.object({
        email: yup.string()
            .required(t('validation.person.email.required'))
            .max(50, t('validation.person.email.emailLength'))
            .email(t('validation.person.email.valid'))
            .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$/, t('validation.person.email.valid')),
        description: yup.string()
            .required(t('validation.person.email.descriptionLength'))
            .max(50, t('validation.person.email.descriptionLength')),
        emailMaxCount: yup.number()
            .max(1, t('validation.person.email.emailMaxCount'))
    });

    const {
        register: emailFormRegister,
        formState: { errors: emailErrors },
        setValue: setEmailValue,
        handleSubmit,
        reset
    } = useForm({
        resolver: yupResolver(emailValidationSchema),
        defaultValues: {
            email: '',
            description: '',
        }
    });


    const fetchEmails = async () => {
        try {
            if (person?.id) {
                const data = await personService.getPersonEmails(person.id);
                setEmailData(data?.email ? data : null);
            }
        } catch (error) {   
            console.error('Error fetching emails:', error);
            setEmailData(null);
            notifyError(t('persons.email.fetchError'));
        }
    };

    const isAddDisabled = Boolean(emailData?.email);

    useEffect(() => {
        fetchEmails();
    }, [person]);

    useEffect(() => {
        if (isAddDisabled) {
            setValidationMessage(t('validation.person.email.emailMaxCount'));
        } else {
            setValidationMessage('');
        }
    }, [isAddDisabled, t]);

    const submitEmailForm = async (formData) => {
        try {
            if (!person?.id) {
                notifyError(t('persons.email.personIdRequired'));
                return false;
            }

            const payload = {
                email: formData.email,
                emailType: formData.description,
                personId: person.id
            };

            if (isEditing && editingEmail) {
                await personService.updatePersonEmail(payload, emailData.id);
                notifySuccess(t('persons.email.updateSuccess'));
            } else {
                await personService.addPersonEmail(payload);
                notifySuccess(t('persons.email.addSuccess'));
            }

            reset({
                email: '',
                description: '',
            });
            setIsEditing(false);
            setEditingEmail(null);
            await fetchEmails();
            return true;
        } catch (error) {
            console.error('Email operation failed:', error);
            notifyError(isEditing 
                ? t('persons.email.updateError')
                : t('persons.email.addError')
            );
            return false;
        }
    };

    const handleEdit = (email) => {
        setIsEditing(true);
        setEditingEmail(email);
        setEmailValue('email', email.email);
        setEmailValue('description', email.emailType);
    };

    const handleDelete = async (emailId) => {
        try {
            await personService.deletePersonEmail(emailId);
            notifySuccess(t('persons.email.deleteSuccess'));
            await fetchEmails();
        } catch (error) {
            notifyError(t('persons.email.deleteError'));
        }
    };

    const handleCancel = () => {
        reset({
            email: '',
            description: '',
        });
        setIsEditing(false);
        setEditingEmail(null);
    };

    return {
        emailData,
        emailFormRegister,
        emailErrors,
        setEmailValue,
        handleEdit,
        handleDelete,
        submitEmailForm,
        isEditing,
        handleSubmit,
        handleCancel,
        isAddDisabled,
        validationMessage
    };
}; 